import {useState, useMemo} from 'react';
import css from './VideoPlayer.module.scss';
import classNames from 'classnames';

const PLATFORM_CONFIG = {
  youtube: {
    label: 'YouTube',
    icon: '▶',
    mode: 'embed',
    getEmbedUrl: (url) => {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?#]+)/);
      return match ? `https://www.youtube.com/embed/${match[1]}` : url;
    },
  },
  rutube: {
    label: 'Rutube',
    icon: '▶',
    mode: 'embed',
    getEmbedUrl: (url) => {
      try {
        const u = new URL(url);
        const privateMatch = u.pathname.match(/\/video\/private\/([^/]+)/);
        if (privateMatch) {
          const id = privateMatch[1];
          const qs = u.search || '';
          return `https://rutube.ru/play/embed/${id}/${qs}`;
        }
        const publicMatch = u.pathname.match(/\/video\/([^/]+)/);
        if (publicMatch) {
          return `https://rutube.ru/play/embed/${publicMatch[1]}`;
        }
      } catch (e) {
        /* ignore */
      }
      const match = url.match(/rutube\.ru\/video\/([^/?#]+)/);
      return match ? `https://rutube.ru/play/embed/${match[1]}` : url;
    },
  },
  vk: {
    label: 'VK Video',
    icon: '▶',
    mode: 'link',
    getPageUrl: (url) => {
      if (url.includes('vkvideo.ru/video_ext.php') || url.includes('vk.com/video_ext.php')) {
        const params = new URLSearchParams(url.split('?')[1]);
        const oid = params.get('oid');
        const id = params.get('id');
        return oid && id ? `https://vkvideo.ru/video${oid}_${id}` : url;
      }
      return url;
    },
  },
  direct: {
    label: 'Video',
    icon: '▶',
    mode: 'direct',
    getEmbedUrl: null,
  },
};

export default function VideoPlayer({sources, basePath}) {
  const platforms = useMemo(() => {
    return sources
      .map(s => ({...s, config: PLATFORM_CONFIG[s.platform]}))
      .filter(s => s.config);
  }, [sources]);

  const [activeTab, setActiveTab] = useState(0);

  if (platforms.length === 0) return null;

  const active = platforms[activeTab];

  const resolveUrl = (url) => {
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
      return url;
    }
    return `${basePath}/${url}`;
  };

  const renderPlayer = () => {
    if (active.config.mode === 'direct') {
      return (
        <video
          key={active.url}
          controls
          className={css.video}
          preload="metadata"
        >
          <source src={resolveUrl(active.url)}/>
        </video>
      );
    }

    if (active.config.mode === 'link') {
      const href = active.config.getPageUrl(active.url);
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={css.linkCard}>
          <div className={css.linkPlayBtn}>▶</div>
          <div className={css.linkText}>
            <span className={css.linkLabel}>Watch on {active.config.label}</span>
            <span className={css.linkUrl}>{href}</span>
          </div>
        </a>
      );
    }

    return (
      <iframe
        key={`${active.platform}-${active.url}`}
        src={active.config.getEmbedUrl(active.url)}
        className={css.iframe}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; screen-wake-lock; web-share"
        allowFullScreen
        title={active.config.label}
      />
    );
  };

  return (
    <div className={css.videoPlayer}>
      {platforms.length > 1 && (
        <div className={css.tabs}>
          {platforms.map((p, i) => (
            <button
              key={p.platform}
              className={classNames(css.tab, {[css.tabActive]: i === activeTab})}
              onClick={() => setActiveTab(i)}
            >
              <span className={css.tabIcon}>{p.config.icon}</span>
              {p.config.label}
            </button>
          ))}
        </div>
      )}

      <div className={classNames(css.playerWrapper, {[css.linkMode]: active.config.mode === 'link'})}>
        {renderPlayer()}
      </div>

      {platforms.length === 1 && (
        <div className={css.singleLabel}>
          {active.config.label}
        </div>
      )}
    </div>
  );
}
